import { Tab as MuiTab, Tabs as MuiTabs } from '@mui/material';
import { r, s } from '../../theme/tokens';
import { useMediaQuery, useTheme } from './hooks';
import type { TabProps as MuiTabProps } from '@mui/material/Tab';
import type { TabsProps as MuiTabsProps } from '@mui/material/Tabs';
import type { ReactElement, SyntheticEvent } from 'react';

type ScrollSnapshot = {
  element: HTMLElement;
  top: number;
  left: number;
};

const getScrollableAncestors = (node: EventTarget | null): ScrollSnapshot[] => {
  if (!(node instanceof HTMLElement)) {
    return [];
  }

  const snapshots: ScrollSnapshot[] = [];
  let current: HTMLElement | null = node;

  while (current) {
    const { overflowY, overflowX, overflow } = window.getComputedStyle(current);
    const canScrollY =
      (overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflow === 'auto' ||
        overflow === 'scroll') &&
      current.scrollHeight > current.clientHeight;
    const canScrollX =
      (overflowX === 'auto' ||
        overflowX === 'scroll' ||
        overflow === 'auto' ||
        overflow === 'scroll') &&
      current.scrollWidth > current.clientWidth;

    if (canScrollY || canScrollX) {
      snapshots.push({
        element: current,
        top: current.scrollTop,
        left: current.scrollLeft,
      });
    }

    current = current.parentElement;
  }

  return snapshots;
};

export type TabsProps = Omit<MuiTabsProps, 'value' | 'onChange'> & {
  scrollableOnMobile?: boolean;
  preserveScrollOnChange?: boolean;
  value?: string | number;
  onChange?: (event: SyntheticEvent, value: string | number) => void;
};

export const Tabs = ({
  scrollableOnMobile = true,
  preserveScrollOnChange = true,
  variant,
  sx,
  onChange,
  ...rest
}: TabsProps): ReactElement => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const effectiveVariant = scrollableOnMobile && isMobile ? 'scrollable' : variant;
  const handleChange = (event: SyntheticEvent, value: string | number): void => {
    if (!preserveScrollOnChange) {
      onChange?.(event, value);
      return;
    }

    const scrollY = typeof window !== 'undefined' ? window.scrollY : null;
    const scrollX = typeof window !== 'undefined' ? window.scrollX : null;
    const ancestorSnapshots =
      typeof window !== 'undefined' ? getScrollableAncestors(event.currentTarget) : [];

    onChange?.(event, value);

    if (scrollY === null || scrollX === null) return;

    const restoreScroll = (): void => {
      ancestorSnapshots.forEach(({ element, top, left }) => {
        element.scrollTop = top;
        element.scrollLeft = left;
      });
      window.scrollTo({ top: scrollY, left: scrollX, behavior: 'auto' });
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        restoreScroll();
        window.requestAnimationFrame(restoreScroll);
      });
    } else {
      window.setTimeout(restoreScroll, 0);
    }

    window.setTimeout(restoreScroll, 150);
  };
  const mergedSx = [
    {
      minHeight: 45,
      color: 'text.primary',
      '& .MuiTabs-flexContainer': { gap: s('sm') },
      '& .MuiTabs-indicator': {
        backgroundColor: 'text.primary',
        height: '1px',
        transition: 'none',
      },
    },
    sx,
  ].filter(Boolean) as MuiTabsProps['sx'];

  return <MuiTabs variant={effectiveVariant} sx={mergedSx} onChange={handleChange} {...rest} />;
};

export type TabProps = MuiTabProps;

export const Tab = ({
  disableFocusRipple = true,
  disableRipple = true,
  sx,
  ...rest
}: TabProps): ReactElement => {
  const mergedSx = [
    {
      backgroundColor: 'transparent',
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '19px',
      lineHeight: 1.4,
      minHeight: 45,
      height: 45,
      paddingTop: 0,
      paddingBottom: 0,
      paddingX: s('md'),
      borderRadius: r('pill'),
      color: 'text.primary',
      '&:hover, &.Mui-focusVisible, &:active': {
        backgroundColor: 'transparent',
      },
      '&.Mui-selected': {
        color: 'text.primary',
        backgroundColor: 'transparent',
      },
      '&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible, &.Mui-selected:active': {
        backgroundColor: 'transparent',
      },
    },
    sx,
  ].filter(Boolean) as MuiTabProps['sx'];

  return (
    <MuiTab
      disableFocusRipple={disableFocusRipple}
      disableRipple={disableRipple}
      sx={mergedSx}
      {...rest}
    />
  );
};
